"use client";

import { useEffect, useRef } from "react";

type AutoPlayVideoProps = {
  src: string;
  poster?: string;
  className?: string;
};

type ConnectionNavigator = Navigator & { connection?: { saveData?: boolean } };

export default function AutoPlayVideo({ src, poster, className = "" }: AutoPlayVideoProps) {
  const ref = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const video = ref.current;
    if (!video) return;

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const saveData = (navigator as ConnectionNavigator).connection?.saveData;
    if (reduceMotion || saveData) return;

    video.muted = true;
    video.setAttribute("playsinline", "true");
    video.setAttribute("webkit-playsinline", "true");
    video.setAttribute("autoplay", "true");

    const attemptPlay = () => {
      video.play().catch(() => {});
    };

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          requestAnimationFrame(attemptPlay);
        } else {
          video.pause();
        }
      },
      { rootMargin: "200px", threshold: 0.1 },
    );

    video.addEventListener("loadedmetadata", attemptPlay, { once: true });
    io.observe(video);

    return () => {
      io.disconnect();
      video.removeEventListener("loadedmetadata", attemptPlay);
    };
  }, [src]);

  return (
    <video
      ref={ref}
      src={src}
      poster={poster}
      autoPlay
      muted
      loop
      playsInline
      preload="auto"
      disablePictureInPicture
      disableRemotePlayback
      aria-hidden="true"
      tabIndex={-1}
      className={className}
    />
  );
}